import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { Subject, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';

import scrollIntoView from 'scroll-into-view-if-needed';
import { Memo } from 'shared/models';

import { UIStateService } from '../../services/ui-state.service';
import { EventBusService } from '../../services/event-bus.service';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { OnlineReadingMemberService } from '../../services/online-reading-member.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { MemoReadStatusService } from '../../services/memo-read-status.service';

import { getMemoGroupType } from '../../utils/memo';

import { RightPanelComponent } from '../right-panel/right-panel.component';

interface MemoGroupView {
  text: string;
  url: string;
  memos: Memo[];
  type: 'mine' | 'others' | 'mixed';
}

@Component({
  selector: 'viewer-item-memo-list',
  templateUrl: './item-memo-list.component.html',
  styleUrls: ['./item-memo-list.component.scss'],
})
export class ItemMemoListComponent implements OnInit, AfterViewInit, OnDestroy {
  public _memoGroups?: MemoGroupView[][];
  public _activeGroupIndex: number = -1;
  public _currentIid?: string;
  public _pageIids: string[] = [];

  public _showMine = false;
  public _canShowMine = true;

  public _onlineUserMap: { [userId: string]: boolean } = {};

  private _unsubscriber = new Subject<void>();

  public _isCommentaryEditorMode = false;

  @ViewChildren('group') groupElemQueryList!: QueryList<
    ElementRef<HTMLDivElement>
  >;

  @ViewChild('bodyContainer', { static: false })
  bodyContainer?: ElementRef;

  constructor(
    private _element: ElementRef,
    private _eventBus: EventBusService,
    private _annotationsService: AnnotationsV2Service,
    private _uiStateService: UIStateService,
    private _rightPanelComponent: RightPanelComponent,
    private _onlineReadingMemberService: OnlineReadingMemberService,
    private _readingGroupsService: ReadingGroupsService,
    private _memoReadStatusService: MemoReadStatusService
  ) {}

  get _totalMemoCount(): number {
    return (
      this._memoGroups?.reduce(
        (result, group) =>
          result +
          group.reduce((result, group) => result + group.memos.length, 0),
        0
      ) ?? 0
    );
  }

  get _myMemoCount(): number {
    return (
      this._memoGroups?.reduce(
        (result, group) =>
          result +
          group.reduce((result, group) => {
            // mine 타입 그룹은 모든 메모가 내 메모
            if (group.type === 'mine') {
              return result + group.memos.length;
            }
            // mixed 타입 그룹은 내 메모만 카운트
            if (group.type === 'mixed') {
              return result + group.memos.filter((memo) => memo.is_mine).length;
            }
            // others 타입 그룹은 0개
            return result;
          }, 0),
        0
      ) ?? 0
    );
  }

  ngOnInit(): void {
    this._annotationsService.state$
      .pipe(
        takeUntil(this._unsubscriber),
        map((state) => ({
          showMine: state.showMine,
          groupId: state.groupId,
          commentaryId: state.commentaryId, // commentaryId 추가
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.showMine === curr.showMine &&
            prev.groupId === curr.groupId &&
            prev.commentaryId === curr.commentaryId // commentaryId 비교 추가
        )
      )
      .subscribe(({ showMine, groupId, commentaryId }) => {
        if (!showMine) {
          this._showMine = false;
          this._canShowMine = false;
        } else {
          const isAloneReading = groupId == null;
          const hasCommentary = commentaryId != null; // 코멘테이터 포함 여부 판단

          if (isAloneReading && !hasCommentary) {
            this._canShowMine = false;
          } else {
            this._canShowMine = true;
          }
        }
      });

    this._eventBus
      .on('ContentsComponent:memoIndicatorClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ iid, groupIndex }) => {
        if (this._uiStateService.canUseMemoSidebar()) {
          this._open(iid, groupIndex);
        } else {
          this.close(false);
        }
      });

    this._eventBus
      .on('ContentsComponent:itemLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ items }) => {
        this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;

        this._memoGroups = undefined;
        this._pageIids = items.map((item) => item.iid).reverse();
      });

    this._eventBus
      .on('MemoPopupComponent:seeMemoListButtonClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ iid, groupIndex }) => {
        this._open(iid, groupIndex);
      });

    this._onlineReadingMemberService.onlineStatusChange$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._onlineUserMap[data.userId] = data.isOnline;
      });

    this._eventBus
      .on('RightPanelComponent:closed')
      .pipe(
        filter(({ activePanel }) => activePanel === 'myAnnotations'),
        takeUntil(this._unsubscriber)
      )
      .subscribe(() => {
        this._readingGroupsService.refreshGroupInfo();
      });

    // 사용자 차단 후 메모 리스트 새로고침
    this._eventBus
      .on('UserBlocked:refreshMemoLists')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._updateMemoGroups(); // 메모 그룹 업데이트
      });
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  ngAfterViewInit(): void {
    if (this.bodyContainer) {
      this.bodyContainer.nativeElement.addEventListener('scroll', () => {
        this._closeAllMenus();
      });
    }
  }

  private _open(iid: string, groupIndex: number): void {
    this._rightPanelComponent.open('myAnnotations');

    this._updateMemoGroups();

    this._currentIid = iid;
    this._activeGroupIndex = groupIndex;
    this._showMine = false;

    setTimeout(() => {
      this._scrollIntoActiveGroup();
    });
  }

  private _updateMemoGroups(): void {
    this._onlineUserMap = {};

    this._memoGroups = this._pageIids.map((iid) => {
      return this._annotationsService.getMemosOfItem(iid).map((group) => {
        group.forEach((m) => {
          this._onlineUserMap[m.author.id] =
            this._onlineReadingMemberService.isOnline(m.author.id);
        });

        const shortMemo = group.reduce((prev, current) =>
          prev && prev.text.length < current.text.length ? prev : current
        );

        return {
          text: shortMemo.text,
          url: shortMemo.url,
          memos: group,
          type: getMemoGroupType(group),
        };
      });
    });
  }

  private _scrollIntoActiveGroup(): void {
    if (!this._memoGroups) {
      return;
    }

    const currGroupIndex = this._pageIids.findIndex(
      (iid) => iid === this._currentIid
    );

    const groupElem = this.groupElemQueryList.get(
      (currGroupIndex > 0 ? this._memoGroups![currGroupIndex - 1].length : 0) +
        this._activeGroupIndex
    );

    if (!groupElem) {
      return;
    }

    scrollIntoView(groupElem.nativeElement, {
      // behavior: 'smooth',
      block: 'center',
      boundary: this._element.nativeElement,
    });
  }

  close(emitEvent: boolean = true): void {
    if (!this._rightPanelComponent.isOpened('myAnnotations')) {
      return;
    }

    this._rightPanelComponent.close(emitEvent);
  }

  _onMemoDeleted(itemIndex: number, groupIndex: number): void {
    const group = this._memoGroups?.[itemIndex][groupIndex];

    if (!group) {
      return;
    }

    if (group.memos.length === 1) {
      this.close();
    } else {
      this._updateMemoGroups();
    }
  }

  _onMemoBlinded(memo: Memo): void {
    this.close();
  }

  _onMemoUserBlocked(memo: Memo): void {
    this.close();
  }

  _onGroupClick(itemIndex: number, index: number): void {
    const group = this._memoGroups?.[itemIndex][index];

    if (!group) {
      return;
    }

    this._currentIid = this._pageIids[itemIndex];
    this._activeGroupIndex = index;

    this._eventBus.fire('ItemMemoListComponent:groupClicked', {
      url: group.url,
      type: group.type === 'mixed' ? 'double' : 'single',
    });

    if (!this._uiStateService.canUseMemoSidebar()) {
      this.close(false);
    }
  }

  _onMemoSeeDetailsButtonClicked(): void {
    if (!this._uiStateService.canUseMemoSidebar()) {
      this.close(false);
    }
  }

  _onMemoPinOptionChanged(): void {
    this._updateMemoGroups();
  }

  _onMemoRead(memo: Memo): void {
    !memo.is_read && this._memoReadStatusService.read(memo.id);
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }
}
